import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { serializer, deserializer } from './serializers';
import { DebugLoggingContextValue, LogMessage } from './types';

const DEBUG_LOG_CACHE_KEY = '_debug_logs';

const DebugLoggingContext = createContext<DebugLoggingContextValue>({
  logMessages: [],
  addLog: () => undefined,
  enabled: false,
});

const DebugLoggingProvider = ({
  children,
  enabled = false,
}: {
  readonly children: ReactNode;
  readonly enabled?: boolean;
}) => {
  const [logMessages, setLogMessages] = useLocalStorage<LogMessage[]>(DEBUG_LOG_CACHE_KEY, [], {
    serializer,
    deserializer,
  });
  const addLog = useCallback(
    (msg: string, context?: unknown) => {
      if (enabled) {
        setLogMessages((existing) => [{ msg, context, timestamp: new Date() }, ...existing]);
      }
    },
    [setLogMessages, enabled]
  );

  const value = useMemo<DebugLoggingContextValue>(
    () => ({
      addLog,
      logMessages: [...logMessages],
      enabled,
    }),
    [logMessages, addLog, enabled]
  );

  return <DebugLoggingContext.Provider value={value}>{children}</DebugLoggingContext.Provider>;
};

const useDebugLogging = () => useContext(DebugLoggingContext).addLog;
const useDebugLogs = () => useContext(DebugLoggingContext).logMessages;
const useDebugLogsEnabled = () => useContext(DebugLoggingContext).enabled;

export { DebugLoggingProvider, useDebugLogging, useDebugLogs, useDebugLogsEnabled };
